import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash.debounce'
import Svg from '../../../../../common/svgIcon';
import createCurrencyFormatter from '../../../../../common/utils/formFormatters';
import BestOddsGuaranteed from '../../../../../common/bestOddsGuaranteed';
import formatDecimalPrice from '../../../../../common/utils/formatDecimalPrice';
import QuickStake from './components/QuickStake';
import NumericKeypad from '../../../../../common/numericKeypad';
import StakeField from '../../../../../common/stakeField';
import ErrorBanner from '../../../../../common/errorBanner';
import EachWay from '../../../../../common/eachWay';
import { calculateReturn } from '../../../../../common/utils/estimatedReturn';
import formatReturns from '../../../../../common/utils/formatReturns';
import { ERROR_CODES, ERROR_MESSAGES } from '../../../../../common/utils/apiErrors'
import cleanStakeInput from '../../../../../common/utils/cleanStakeInput'
import PRICE_TYPES from '../../../../../common/utils/priceTypes'
import './styles.scss';

const BANNER_DISPLAY_DELAY = 800

export class BetForm extends Component {

    onStakeUpdated = (rawStakeValue) => {
        this.dismissStakeErrors(rawStakeValue)
        this.displayStakeErrors(rawStakeValue)
    }

    onToggleEachWay = (e) => {
        const {
            outcomersId,
            setIsEachWay,
        } = this.props;

        setIsEachWay(outcomersId, e.target.checked);
    }

    onTogglePriceType = (e) => {
        const {
            outcomersId,
            setPriceType,
        } = this.props;

        setPriceType(outcomersId, e.target.value);
    }

    onFormSubmit = (e) => {
        e.preventDefault();
    }

    getStakeInfo(rawStakeValue) {
        const {
            associatedBet,
            freeBetIsSelected,
        } = this.props

        let minStake = associatedBet.minStake
        let maxStake = associatedBet.maxStake

        // Use freeBet min/max stake values if free bet is selected
        if (freeBetIsSelected) {
            minStake = (associatedBet.freeBetMinStake)
                ? associatedBet.freeBetMinStake
                : minStake

            maxStake = (associatedBet.freeBetMaxStake)
                ? associatedBet.freeBetMaxStake
                : maxStake
        }

        const cleaned = parseFloat(cleanStakeInput(rawStakeValue))
        const newStake = (isNaN(cleaned)) ? 0 : cleaned

        return {
            minStake,
            maxStake,
            newStake,
        }
    }

    getPriceType = () => this.props.priceType ||
            (this.props.isSpOnly ? PRICE_TYPES.SP : PRICE_TYPES.ODDS)

    getEstimatedReturnText = () => {
        const {
            isSpOnly,
            priceType,
            stakeValue,
            isEachWay,
            fractionalPrice,
            eachWayFraction,
            currencySign,
        } = this.props

        const spBet = priceType === PRICE_TYPES.SP || isSpOnly === true

        // Always display 'N/A' if any SP bets are detected in selections
        if (spBet) {
            return 'N/A'
        }

        // For all other scenarios, let's calculate it!
        const parsedStake = Number.parseFloat(stakeValue) || 0
        const betReturn = calculateReturn(
            parsedStake,
            fractionalPrice,
            isEachWay && eachWayFraction,
        )
        const formattedReturns = formatReturns(betReturn)
        const currencyFormattedReturns = createCurrencyFormatter(currencySign)(formattedReturns)

        return currencyFormattedReturns
    }

    dismissStakeErrors = (rawStakeValue) => {
        const {
            outcomersId,
            error,
            associatedBet,
            clearError,
        } = this.props

        // If we have min/max stake values, and user inputs a stake,
        // dismiss any min/max stake errors, if appropriate.
        if (associatedBet && rawStakeValue) {
            const {
                minStake,
                maxStake,
                newStake,
            } = this.getStakeInfo(rawStakeValue)

            const shouldClearMinError =
                minStake !== null &&
                error.errorId === ERROR_CODES.STAKE_TOO_LOW &&
                newStake >= minStake

            const shouldClearMaxError =
                maxStake !== null &&
                error.errorId === ERROR_CODES.STAKE_TOO_HIGH &&
                newStake <= maxStake

            if (shouldClearMinError || shouldClearMaxError) {
                clearError(outcomersId)
            }
        }
    }

    displayStakeErrors = debounce((rawStakeValue) => {
        const {
            outcomersId,
            associatedBet,
            currencySign,
            setErrorOnBuild,
        } = this.props

        // If we have min/max stake values, and user inputs a stake,
        // dismiss any min/max stake errors, if appropriate.
        if (associatedBet && rawStakeValue) {
            const {
                minStake,
                maxStake,
                newStake,
            } = this.getStakeInfo(rawStakeValue)

            // Restore a stake-too-low error
            if (minStake !== null && newStake < minStake) {
                const message = ERROR_MESSAGES.STAKE_TOO_LOW
                    .replace('%PRICE%', `${currencySign}${minStake.toFixed(2)}`)
                setErrorOnBuild(
                    outcomersId,
                    ERROR_CODES.STAKE_TOO_LOW,
                    message,
                )
            }

            // Restore a stake-too-high error
            if (maxStake !== null && newStake > maxStake) {
                const message = ERROR_MESSAGES.STAKE_TOO_HIGH
                    .replace('%PRICE%', `${currencySign}${maxStake.toFixed(2)}`)
                setErrorOnBuild(
                    outcomersId,
                    ERROR_CODES.STAKE_TOO_HIGH,
                    message,
                )
            }
        }
    }, BANNER_DISPLAY_DELAY)

    toggleMultiples = (event) => {
        const {
            outcomersId,
            toggleMultiplesInclusion,
        } = this.props
        const include = !!event.target.checked
        toggleMultiplesInclusion(outcomersId, include)
    }

    renderQuickStake = (disableStakeInput) => {
        const {
            currencySign,
            stakeValue,
            isMobile,
            outcomersId,
            numberOfBets,
            setStake,
            bookmakerName,
            betslipID,
            horseName,
            fractionalPrice,
            raceId,
            courseName,
            raceTime,
            raceDate,
        } = this.props;

        if (!disableStakeInput && numberOfBets === 1) {
            return (
                <QuickStake
                    currencySign={currencySign}
                    setStake={setStake}
                    onStakeUpdated={this.onStakeUpdated}
                    value={stakeValue}
                    maxLength={8}
                    isMobile={isMobile}
                    outcomersId={outcomersId}
                    bookmakerName={bookmakerName}
                    betslipID={betslipID}
                    horseName={horseName}
                    fractionalPrice={fractionalPrice}
                    raceId={raceId}
                    courseName={courseName}
                    raceTime={raceTime}
                    raceDate={raceDate}
                />
            );
        }

        return null;
    }

    renderPriceSwitcher = () => {
        const {
            isSpOnly,
            useDecimalOdds,
            decimalPrice,
            fractionalPrice,
            isSpAvailable,
            outcomersId,
        } = this.props;
        const priceType = this.getPriceType();
        const price = useDecimalOdds ? formatDecimalPrice(decimalPrice) : fractionalPrice;

        return (
            <div className="bs-betForm__priceSwitcher">
                <div
                    className={classNames(
                        'bs-betForm__priceSwitcher-button',
                        { 'bs-betForm__priceSwitcher_disabled': isSpOnly },
                    )}
                >
                    { !isSpOnly ?
                        <div
                            className={classNames(
                                'bs-betForm__priceButton',
                                { 'bs-betForm__priceButton_sp-unavailable': !isSpAvailable },
                            )}
                        >
                            <input
                                name="priceType"
                                id={`bs-betForm-odds-radio-${outcomersId}`}
                                type="radio"
                                onChange={this.onTogglePriceType}
                                className="bs-betForm__priceInput"
                                checked={priceType === PRICE_TYPES.ODDS}
                                value={PRICE_TYPES.ODDS}
                                disabled={isSpOnly}
                                tabIndex={-1}
                            />
                            <label
                                htmlFor="bs-betForm-odds-radio"
                                className="bs-betForm__priceLabel"
                            >
                                <span className="bs-betForm__priceLabel-label-copy">
                                    {price}
                                </span>
                            </label>
                        </div>
                    : null }
                    { isSpAvailable ? (
                        <div className="bs-betForm__priceButton">
                            <input
                                name="priceType"
                                id={`bs-betForm-sp-radio-${outcomersId}`}
                                type="radio"
                                onChange={this.onTogglePriceType}
                                className="bs-betForm__priceInput"
                                checked={priceType === PRICE_TYPES.SP}
                                value={PRICE_TYPES.SP}
                                disabled={isSpOnly}
                                tabIndex={-1}
                            />
                            <label
                                htmlFor="bs-betForm-sp-radio"
                                className="bs-betForm__priceLabel"
                            >
                                <span className="bs-betForm__priceLabel-label-copy">
                                    SP
                                </span>
                            </label>
                        </div>
                    ) : null }
                </div>
            </div>
        );
    }

    renderEachWay = (disableStakeInput) => {
        const {
            eachWayFraction,
            eachWayPlaces,
            isEachWay,
            setGlobalModal,
            outcomersId,
        } = this.props;
        const {
            onToggleEachWay,
        } = this;

        const isWinOnly = eachWayFraction === '1/1' && eachWayPlaces === '1-1';
        const isEachWayEnabled = (eachWayFraction || eachWayPlaces)
            && !disableStakeInput
            && !isWinOnly;

        return (
            <EachWay
                id={outcomersId}
                isEachWayChecked={isEachWay}
                isEachWayEnabled={isEachWayEnabled}
                onChange={onToggleEachWay}
                setGlobalModal={setGlobalModal}
            >
                <div className="bs-eachWay-value">
                    <span className="bs-eachWay-line">
                        {eachWayFraction}
                    </span>
                    <span className="bs-eachWay-line">
                        {eachWayPlaces}
                    </span>
                </div>
            </EachWay>
        )
    }

    renderNumericKeyPad = () => {
        const {
            isMobile,
            stakeValue,
            outcomersId,
            isEditingStake,
            setIsEditingStake,
            setStake,
        } = this.props

        return (isMobile && isEditingStake) ? (
            <NumericKeypad
                targetId={outcomersId}
                setIsEditingStake={setIsEditingStake}
                setStake={setStake}
                onStakeUpdated={this.onStakeUpdated}
                value={stakeValue}
                maxLength={8}
            />
            )
                : null;
    }

    renderCloseButton = () => {
        const {
            numberOfBets,
            isQuickslipMode,
            outcomersId,
            onCloseClick,
        } = this.props

        if ((isQuickslipMode && numberOfBets > 1) || !isQuickslipMode) {
            return (
                <button
                    type="button"
                    className="bs-betForm__deleteSelect"
                    onClick={
                        () => {
                            onCloseClick(outcomersId);
                        }
                    }
                >
                    <Svg
                        iconName="remove"
                        size="SmallMedium"
                    />
                </button>
            );
        }

        return null;
    }

    renderMultiplesRow = () => {
        const {
            isQuickslipMode,
            outcomersId,
            includeInMultiples,
        } = this.props

        if (isQuickslipMode) {
            return null
        }

        return (
            <div className="bs-betForm__row bs-betForm__row-inlineReturns">
                <div className="bs-betForm__multiplesToggle">
                    <input
                        id={`bs-betForm-multiples-checkbox-${outcomersId}`}
                        type="checkbox"
                        checked={includeInMultiples ? 'checked' : ''}
                        onChange={this.toggleMultiples}
                        className="bs-betForm__multiplesToggle-input"
                    />
                    <Svg
                        iconName="multiples-tick"
                        size="Large"
                        className={classNames(
                            'bs-betForm__multiplesToggle-tick',
                            { 'bs-betForm__multiplesToggle-tick-checked': includeInMultiples },
                        )}
                    />
                    <label
                        htmlFor={`bs-betForm-multiples-checkbox-${outcomersId}`}
                        className="bs-betForm__multiplesToggle-label"
                    >
                    Multiples
                    </label>
                </div>
                <div className="">
                    <span>Est. Returns:</span>
                    <span className="bs-betForm__estReturns">{this.getEstimatedReturnText()}</span>
                </div>
            </div>
        )
    }

    render() {
        const {
            outcomersId,
            horseName,
            raceTime,
            courseName,
            className,
            freezeStake,
            isBog,
            error,
            isSuspended,
            setIsEditingStake,
            stakeValue,
            currencySign,
            isMobile,
            setStake,
            isEditingStake,
            isEachWay,
            priceType,
        } = this.props

        const errorBanner = (error.message !== null) ?
            <ErrorBanner message={error.message} />
            : null;
        return (
            <form
                className={classNames(
                    'bs-betForm',
                    className,
                    { 'bs-betForm__isSuspended': isSuspended },
                )}
                onSubmit={this.onFormSubmit}
            >
                {errorBanner}
                <div className="bs-betForm__body">
                    {this.renderCloseButton()}
                    <div className="bs-betForm__row bs-betForm__row-horseName">
                        <span className="bs-betForm__horseName">{horseName}</span>
                    </div>
                    <div className="bs-betForm__row bs-betForm__row-raceInfo">
                        <div className="bs-betForm__raceInfo">
                            <span>{raceTime}</span>
                            <span className="bs-betForm__courseName">{courseName}</span>
                        </div>
                        <BestOddsGuaranteed
                            visible={isBog === 'Y' && priceType !== PRICE_TYPES.SP}
                        />
                    </div>
                    <div className="bs-betForm__row bs-betForm__row-stake">

                        { this.renderPriceSwitcher() }
                        { this.renderEachWay(freezeStake) }

                        <StakeField
                            targetId={outcomersId}
                            currencySign={currencySign}
                            stakeValue={stakeValue}
                            setStake={setStake}
                            setIsEditingStake={setIsEditingStake}
                            isEditingStake={isEditingStake}
                            onStakeUpdated={this.onStakeUpdated}
                            isMobile={isMobile}
                            placeholder={isEachWay ? 'Unit Stake' : 'Stake'}
                            freezeStake={freezeStake}
                        />
                    </div>
                    { this.renderMultiplesRow() }
                    { this.renderNumericKeyPad() }
                    { this.renderQuickStake(freezeStake) }
                </div>
            </form>
        );
    }
}

BetForm.defaultProps = {
    bookmakerName: null,
    betslipID: null,
    raceId: null,
    raceDate: null,
    horseName: null,
    raceTime: null,
    courseName: null,
    fractionalPrice: null,
    eachWayFraction: null,
    eachWayPlaces: null,
    priceType: null,
    stakeValue: '',
    className: '',
    isNumericKeypadVisible: false,
    isMobile: false,
    isEachWay: false,
    numberOfBets: 0,
    error: {
        message: null,
        errorId: null,
    },
    decimalPrice: null,
    genericError: {
        message: null,
        errorId: null,
    },
    associatedBet: null,
}

BetForm.propTypes = {
    bookmakerName: PropTypes.string,
    betslipID: PropTypes.string,
    raceId: PropTypes.string,
    raceDate: PropTypes.string,
    horseName: PropTypes.string,
    raceTime: PropTypes.string,
    courseName: PropTypes.string,
    fractionalPrice: PropTypes.string,
    decimalPrice: PropTypes.number,
    useDecimalOdds: PropTypes.bool.isRequired,
    isEachWay: PropTypes.bool.isRequired,
    isBog: PropTypes.string.isRequired,
    freezeStake: PropTypes.bool.isRequired,
    currencySign: PropTypes.string.isRequired,
    isSpOnly: PropTypes.bool.isRequired,
    isSpAvailable: PropTypes.bool.isRequired,
    eachWayFraction: PropTypes.string,
    eachWayPlaces: PropTypes.string,
    stakeValue: PropTypes.string,
    priceType: PropTypes.string,
    className: PropTypes.string,
    isMobile: PropTypes.bool,
    setIsEachWay: PropTypes.func.isRequired,
    setPriceType: PropTypes.func.isRequired,
    onCloseClick: PropTypes.func.isRequired,
    setStake: PropTypes.func.isRequired,
    outcomersId: PropTypes.string.isRequired,
    numberOfBets: PropTypes.number.isRequired,
    setIsEditingStake: PropTypes.func.isRequired,
    isEditingStake: PropTypes.bool.isRequired,
    isQuickslipMode: PropTypes.bool.isRequired,
    isSuspended: PropTypes.bool.isRequired,
    error: PropTypes.shape({
        message: PropTypes.string,
        errorId: PropTypes.string,
    }),
    associatedBet: PropTypes.shape({
        optionId: PropTypes.string.isRequired,
        minStake: PropTypes.number,
        maxStake: PropTypes.number,
        numberLines: PropTypes.number.isRequired,
    }),
    clearError: PropTypes.func.isRequired,
    setErrorOnBuild: PropTypes.func.isRequired,
    setGlobalModal: PropTypes.func.isRequired,
    includeInMultiples: PropTypes.bool.isRequired,
    toggleMultiplesInclusion: PropTypes.func.isRequired,
    freeBetIsSelected: PropTypes.bool.isRequired,
}

export default BetForm



// WEBPACK FOOTER //
// /src/js/drawer/components/BetPage/components/BetForm/BetForm.jsx